/** How long to wait before the next save: a short debounce, then exponential backoff after failures. */
export function autosaveDelay(attempt: number): number {
  if (attempt === 0) return 600;
  return Math.min(30_000, 1000 * 2 ** (attempt - 1));
}

export type SaveState = 'saved' | 'saving' | 'failed';

export interface AutosaverOptions<T> {
  readonly save: (doc: T) => Promise<void>;
  readonly onState: (state: SaveState) => void;
  /** Injected so tests can drive time; defaults to the globals. */
  readonly setTimeout?: typeof globalThis.setTimeout;
  readonly clearTimeout?: typeof globalThis.clearTimeout;
}

/**
 * Saves the newest document after a debounce, backs off after failures,
 * and never has two saves in flight: an edit made during a save is saved
 * once that save settles. A document already saved is not saved again.
 */
export class Autosaver<T> {
  readonly #save: (doc: T) => Promise<void>;
  readonly #onState: (state: SaveState) => void;
  readonly #setTimeout: typeof globalThis.setTimeout;
  readonly #clearTimeout: typeof globalThis.clearTimeout;

  #latest: T | undefined;
  #lastSaved: T | undefined;
  #attempt = 0;
  #timer: ReturnType<typeof globalThis.setTimeout> | null = null;
  #inFlight: Promise<void> | null = null;
  #disposed = false;

  constructor(options: AutosaverOptions<T>, alreadySaved?: T) {
    this.#save = options.save;
    this.#onState = options.onState;
    this.#setTimeout = options.setTimeout ?? globalThis.setTimeout.bind(globalThis);
    this.#clearTimeout = options.clearTimeout ?? globalThis.clearTimeout.bind(globalThis);
    this.#latest = alreadySaved;
    this.#lastSaved = alreadySaved;
  }

  /** The document changed; schedule a save of whatever is newest when the timer fires. */
  notify(doc: T): void {
    this.#latest = doc;
    if (doc === this.#lastSaved) return;
    this.#schedule();
  }

  /**
   * Stop the pending timer and any future scheduling; an in-flight save still
   * settles, but its result is discarded rather than retried or reported —
   * otherwise a save that fails after dispose would retry forever against a
   * store that may no longer be valid (e.g. after unmount or sign-out).
   */
  dispose(): void {
    this.#disposed = true;
    if (this.#timer === null) return;
    this.#clearTimeout(this.#timer);
    this.#timer = null;
  }

  #schedule(): void {
    if (this.#disposed) return;
    if (this.#timer !== null) this.#clearTimeout(this.#timer);
    this.#timer = this.#setTimeout(() => {
      this.#timer = null;
      this.#fire();
    }, autosaveDelay(this.#attempt));
  }

  #fire(): void {
    if (this.#disposed) return;
    // A save is already running; its own completion decides whether to save again.
    if (this.#inFlight) return;
    const doc = this.#latest as T;
    this.#onState('saving');
    this.#inFlight = this.#save(doc)
      .then(() => {
        if (this.#disposed) return;
        this.#lastSaved = doc;
        this.#attempt = 0;
        this.#onState('saved');
      })
      .catch(() => {
        if (this.#disposed) return;
        this.#attempt += 1;
        this.#onState('failed');
      })
      .finally(() => {
        this.#inFlight = null;
        if (this.#disposed) return;
        // An edit landed while saving, or this attempt failed: try again.
        if (this.#latest !== this.#lastSaved) this.#schedule();
      });
  }
}
